// @ts-nocheck
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { typographyInvertColorStyles } from "@store/Themes/common/mixins/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import setCookieDrawerIsOpen from "@store/Framework/Store/Greif/Components/CookiePrivacyPolicy/Handlers/SetCookieDrawerIsOpen";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import { linkStyles } from "@store/Themes/common/mixins/link";
/* Store Custom Code - End */

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
    // Starter Custom Code
    actionWrapper?: InjectableCss;
    closeLink?: LinkPresentationProps;
    // Store Custom Code
    innerWrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    messageTitle?: TypographyPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        cssOverrides: {
            scrim: css`
                height: auto;
                /* Store Custom Code - Start */
                border-radius: ${pxToRem(8)} ${pxToRem(8)} 0 0;
                /* Store Custom Code - End */
            `,
            drawerBody: css`
                position: relative;
                height: auto;
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                /* Store Custom Code - Start */
                background: linear-gradient(90deg, #15376b, #2d69bd);
                /* Store Custom Code - End */
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    messageText: {
        variant: "p",
        css: css`
            overflow-x: hidden;
            overflow-y: visible;
            max-height: 50vh;
            flex-grow: 1;
            margin: 0;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex-shrink: 0;
            gap: inherit;
        `,
    },
    acceptButton: {
        variant: "inverted" as any,
    },
    /* Store Custom Code - Start */
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;
            gap: ${pxToRem(15, 30)};
            width: 100%;
            padding-top: ${pxToRem(20)};
            padding-bottom: ${pxToRem(20)};

            ${breakpoint("md")} {
                flex-direction: row;
                align-items: center;
                padding-top: ${pxToRem(30)};
                padding-bottom: ${pxToRem(30)};
            }
        `,
    },
    messageWrapper: {
        css: css`
            ${typographyInvertColorStyles()}
            max-width: ${pxToRem(675)};
        `,
    },
    messageTitle: {
        forwardAs: "p",
        css: css`
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: ${pxToRem(10)};
        `,
    },
    closeLink: {
        css: css`
            ${linkStyles("text.disabled", null, false, true)}
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    /* Store Custom Code - End */
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    /* Store Custom Code - Start */
    const cookieDrawerIsOpen = useAppSelector(state => state.greif.components.cookiePrivacyPolicy.isOpen);
    const dispatch = useAppDispatch();
    /* Store Custom Code - End */
    const [isOpen, setIsOpen] = useState(cookieDrawerIsOpen); // Store Custom Code

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
            /* Store Custom Code - Start */
            dispatch(setCookieDrawerIsOpen({ isOpen: true }));
            /* Store Custom Code - End */
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
        /* Store Custom Code - Start */
        dispatch(setCookieDrawerIsOpen({ isOpen: false }));
        /* Store Custom Code - End */
    };

    const closeHandler = () => {
        setIsOpen(false);
        /* Store Custom Code - Start */
        dispatch(setCookieDrawerIsOpen({ isOpen: false }));
        /* Store Custom Code - End */
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            {/* Starter Custom Code - Start */}
            <StyledWrapper {...styles.innerWrapper} /* Store Custom Code */>
                <StyledWrapper {...styles.messageWrapper} /* Store Custom Code */>
                    {/* Store Custom Code - Start */}
                    <Typography {...styles.messageTitle} data-test-selector="cookiePolicy_PrivacyPolicyHeader">
                        {translate("We use cookies")}
                    </Typography>
                    {/* Store Custom Code - End */}
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.actionWrapper}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                    <Link {...styles.closeLink} onClick={closeHandler} data-test-selector="cookiePolicy_ClosePolicy">
                        {translate("Decline") /* Store Custom Code */}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            {/* Starter Custom Code - End */}
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
